import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FolderTree,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ArrowLeft,
  RefreshCw,
  Loader2,
  Package,
  Search,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react'
import { useNotification } from '../../contexts/NotificationContext'

interface Category {
  id: string
  name: string
  description: string | null
  parent_id: string | null
  parent_name: string | null
  item_count: number
  subcategory_count: number
  field_count?: number
  visible: boolean
  created_at: string
  updated_at: string
}

interface CustomField {
  id: string
  category_id: string
  field_name: string
  field_type: 'text' | 'number' | 'date' | 'boolean' | 'select'
  required: boolean
  sort_order: number
  created_at: string
}

const API_BASE = ''

export default function CategoriesPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useNotification()
  const [categories, setCategories] = useState<Category[]>([])
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_id: '',
    copyParentFields: false
  })
  const [loading, setLoading] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [parentFieldsCount, setParentFieldsCount] = useState(0)

  // Custom fields slideout state
  const [showFieldsSlideout, setShowFieldsSlideout] = useState(false)
  const [selectedCategoryForFields, setSelectedCategoryForFields] = useState<Category | null>(null)
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [loadingFields, setLoadingFields] = useState(false)
  const [showAddFieldDialog, setShowAddFieldDialog] = useState(false)
  const [fieldFormData, setFieldFormData] = useState({
    field_name: '',
    field_type: 'text' as CustomField['field_type'],
    required: false,
    sort_order: 0
  })

  useEffect(() => {
    fetchCategories()
  }, [])

  // Fetch parent field count when parent_id changes
  useEffect(() => {
    const fetchParentFieldCount = async () => {
      if (formData.parent_id) {
        try {
          const response = await fetch(`${API_BASE}/api/category-fields-pg?categoryId=${formData.parent_id}`)
          const data = await response.json()
          if (data.success) {
            setParentFieldsCount(data.data?.length || 0)
          }
        } catch (error) {
          console.error('Error fetching parent field count:', error)
          setParentFieldsCount(0)
        }
      } else {
        setParentFieldsCount(0)
        setFormData(prev => ({ ...prev, copyParentFields: false }))
      }
    }
    fetchParentFieldCount()
  }, [formData.parent_id])

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true)
      const response = await fetch(`${API_BASE}/api/categories-pg`)
      const data = await response.json()

      if (data.success) {
        const categoriesData = data.data || []

        // Fetch field counts for each category
        const categoriesWithFields = await Promise.all(
          categoriesData.map(async (category: Category) => {
            try {
              const fieldsResponse = await fetch(`${API_BASE}/api/category-fields-pg?categoryId=${category.id}`)
              const fieldsData = await fieldsResponse.json()
              return {
                ...category,
                field_count: fieldsData.success ? (fieldsData.data?.length || 0) : 0
              }
            } catch {
              return { ...category, field_count: 0 }
            }
          })
        )

        setCategories(categoriesWithFields)
      } else {
        showError('Failed to load categories')
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      showError('Failed to load categories')
    } finally {
      setLoadingCategories(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      showError('Category name is required')
      return
    }

    setLoading(true)

    try {
      // Create category
      const response = await fetch(`${API_BASE}/api/categories-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          parent_id: formData.parent_id || null
        })
      })

      const data = await response.json()

      if (data.success) {
        const newCategoryId = data.data.id

        // Copy parent fields if requested
        if (formData.copyParentFields && formData.parent_id) {
          try {
            // Fetch parent fields
            const parentFieldsResponse = await fetch(`${API_BASE}/api/category-fields-pg?categoryId=${formData.parent_id}`)
            const parentFieldsData = await parentFieldsResponse.json()

            if (parentFieldsData.success && parentFieldsData.data?.length > 0) {
              // Copy each field to the new category
              const copyPromises = parentFieldsData.data.map((field: CustomField) =>
                fetch(`${API_BASE}/api/category-fields-pg`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    category_id: newCategoryId,
                    name: field.name,
                    field_type: field.field_type,
                    required: field.required,
                    sort_order: field.sort_order
                  })
                })
              )

              await Promise.all(copyPromises)
              success(`Category created successfully with ${parentFieldsData.data.length} custom field(s) copied`)
            } else {
              success('Category created successfully')
            }
          } catch (fieldError) {
            console.error('Error copying parent fields:', fieldError)
            success('Category created successfully (failed to copy some custom fields)')
          }
        } else {
          success('Category created successfully')
        }

        await fetchCategories()
        setFormData({ name: '', description: '', parent_id: '', copyParentFields: false })
        setShowCreateDialog(false)
      } else {
        showError(data.error || 'Failed to create category')
      }
    } catch (error) {
      console.error('Error creating category:', error)
      showError('Failed to create category')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      description: category.description || '',
      parent_id: category.parent_id || '',
      copyParentFields: false
    })
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingCategory) return

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/categories-pg/${editingCategory.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          parent_id: formData.parent_id || null
        })
      })

      const data = await response.json()

      if (data.success) {
        success('Category updated successfully')
        await fetchCategories()
        setEditingCategory(null)
        setFormData({ name: '', description: '', parent_id: '' })
      } else {
        showError(data.error || 'Failed to update category')
      }
    } catch (error) {
      console.error('Error updating category:', error)
      showError('Failed to update category')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (category: Category) => {
    if (!confirm(`Are you sure you want to delete "${category.name}"?\n\nNote: Categories with items or subcategories cannot be deleted.`)) return

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/categories-pg/${category.id}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (response.ok && data.success) {
        success('Category deleted successfully')
        await fetchCategories()
      } else {
        // Show detailed error message from API
        if (response.status === 409 && category.item_count > 0) {
          showError(`Cannot delete "${category.name}". It has ${category.item_count} item(s). Move or delete items first, or use "View Items" to see them.`)
        } else if (response.status === 409 && category.subcategory_count > 0) {
          showError(`Cannot delete "${category.name}". It has ${category.subcategory_count} subcategory(ies). Move or delete subcategories first.`)
        } else {
          showError(data.error || 'Failed to delete category')
        }
      }
    } catch (error: any) {
      console.error('Error deleting category:', error)
      showError(error.message || 'Failed to delete category')
    } finally {
      setLoading(false)
    }
  }

  const handleViewItems = (category: Category) => {
    // Navigate to search page with category filter
    navigate(`/search?category=${category.id}`)
  }

  const handleManageFields = async (category: Category) => {
    setSelectedCategoryForFields(category)
    setShowFieldsSlideout(true)
    await fetchCustomFields(category.id)
  }

  const fetchCustomFields = async (categoryId: string) => {
    setLoadingFields(true)
    try {
      const response = await fetch(`${API_BASE}/api/category-fields-pg?categoryId=${categoryId}`)
      const data = await response.json()
      if (data.success) {
        setCustomFields(data.data || [])
      } else {
        showError('Failed to load custom fields')
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error)
      showError('Failed to load custom fields')
    } finally {
      setLoadingFields(false)
    }
  }

  const handleAddField = async () => {
    if (!selectedCategoryForFields) return
    if (!fieldFormData.field_name.trim()) {
      showError('Field name is required')
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`${API_BASE}/api/category-fields-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_id: selectedCategoryForFields.id,
          ...fieldFormData
        })
      })

      const data = await response.json()

      if (data.success) {
        success('Custom field added successfully')
        setShowAddFieldDialog(false)
        setFieldFormData({ field_name: '', field_type: 'text', required: false, sort_order: 0 })
        await fetchCustomFields(selectedCategoryForFields.id)
        await fetchCategories() // Refresh field counts
      } else {
        showError(data.error || 'Failed to add custom field')
      }
    } catch (error) {
      console.error('Error adding custom field:', error)
      showError('Failed to add custom field')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteField = async (fieldId: string) => {
    if (!confirm('Are you sure you want to delete this custom field?')) return
    if (!selectedCategoryForFields) return

    setLoading(true)
    try {
      const response = await fetch(`${API_BASE}/api/category-fields-pg?id=${fieldId}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        success('Custom field deleted successfully')
        await fetchCustomFields(selectedCategoryForFields.id)
        await fetchCategories() // Refresh field counts
      } else {
        showError(data.error || 'Failed to delete custom field')
      }
    } catch (error) {
      console.error('Error deleting custom field:', error)
      showError('Failed to delete custom field')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleVisibility = async (category: Category) => {
    const newVisibility = !category.visible

    try {
      const response = await fetch(`${API_BASE}/api/categories-pg/${category.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visible: newVisibility
        })
      })

      const data = await response.json()

      if (data.success) {
        success(`Category ${newVisibility ? 'shown' : 'hidden'} from intake/details`)
        await fetchCategories()
      } else {
        showError(data.error || 'Failed to update visibility')
      }
    } catch (error) {
      console.error('Error toggling visibility:', error)
      showError('Failed to update visibility')
    }
  }

  const parentCategories = categories.filter(c => !c.parent_id)

  if (loadingCategories) {
    return (
      <div style={{
        padding: '24px',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 style={{ width: 48, height: 48, color: '#FF6B35', animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#8A95AA', marginTop: '16px' }}>Loading categories...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              CATEGORY MANAGEMENT
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '8px 0 0 0'
            }}>
              MANAGE PRODUCT CATEGORIES AND HIERARCHIES
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(138, 149, 170, 0.1)',
                border: '2px solid rgba(138, 149, 170, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#8A95AA',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              BACK
            </button>
            <button
              onClick={() => setShowCreateDialog(true)}
              style={{
                background: 'rgba(185, 27, 255, 0.2)',
                border: '2px solid #B91BFF',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#B91BFF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 0 20px rgba(185, 27, 255, 0.3)'
              }}
            >
              <Plus style={{ width: 16, height: 16 }} />
              ADD CATEGORY
            </button>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '16px'
      }}>
        {categories.map((category) => (
          <div
            key={category.id}
            style={{
              background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.8), rgba(36, 41, 56, 0.8))',
              border: '2px solid rgba(255, 20, 147, 0.3)',
              borderRadius: '12px',
              padding: '20px',
              transition: 'all 0.3s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: 'rgba(255, 20, 147, 0.2)',
                border: '2px solid #FF1493',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <FolderTree style={{ width: 24, height: 24, color: '#FF1493' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <h3 style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#FF1493',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    margin: 0
                  }}>
                    {category.name}
                  </h3>
                  {!category.visible && (
                    <span style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#8A95AA',
                      background: 'rgba(138, 149, 170, 0.2)',
                      border: '1px solid rgba(138, 149, 170, 0.4)',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <EyeOff style={{ width: 10, height: 10 }} />
                      Hidden
                    </span>
                  )}
                </div>
                {category.parent_name && (
                  <p style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '12px',
                    color: '#B91BFF',
                    margin: '0 0 4px 0'
                  }}>
                    Parent: {category.parent_name}
                  </p>
                )}
                {category.description && (
                  <p style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '13px',
                    color: '#8A95AA',
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    {category.description}
                  </p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '12px',
              paddingTop: '12px',
              borderTop: '1px solid rgba(255, 20, 147, 0.2)'
            }}>
              <div style={{
                flex: 1,
                background: 'rgba(107, 207, 127, 0.1)',
                border: '1px solid rgba(107, 207, 127, 0.3)',
                borderRadius: '6px',
                padding: '8px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#6BCF7F'
                }}>
                  {category.item_count}
                </div>
                <div style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '10px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Items
                </div>
              </div>
              <div style={{
                flex: 1,
                background: 'rgba(185, 27, 255, 0.1)',
                border: '1px solid rgba(185, 27, 255, 0.3)',
                borderRadius: '6px',
                padding: '8px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#B91BFF'
                }}>
                  {category.subcategory_count}
                </div>
                <div style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '10px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Subcategories
                </div>
              </div>
              <div style={{
                flex: 1,
                background: 'rgba(0, 240, 255, 0.1)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                borderRadius: '6px',
                padding: '8px',
                textAlign: 'center'
              }}>
                <div style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#00F0FF'
                }}>
                  {category.field_count || 0}
                </div>
                <div style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '10px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Fields
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {category.item_count > 0 && (
                <button
                  onClick={() => handleViewItems(category)}
                  style={{
                    flex: '1 1 100%',
                    background: 'rgba(107, 207, 127, 0.1)',
                    border: '2px solid rgba(107, 207, 127, 0.4)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: '#6BCF7F',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Search style={{ width: 14, height: 14 }} />
                  VIEW {category.item_count} ITEM{category.item_count !== 1 ? 'S' : ''}
                </button>
              )}
              <button
                onClick={() => handleManageFields(category)}
                style={{
                  flex: '1 1 100%',
                  background: 'rgba(0, 240, 255, 0.1)',
                  border: '2px solid rgba(0, 240, 255, 0.4)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#00F0FF',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Settings style={{ width: 14, height: 14 }} />
                MANAGE {category.field_count || 0} FIELD{category.field_count !== 1 ? 'S' : ''}
              </button>
              <button
                onClick={() => handleToggleVisibility(category)}
                style={{
                  flex: 1,
                  background: category.visible ? 'rgba(107, 207, 127, 0.1)' : 'rgba(138, 149, 170, 0.1)',
                  border: category.visible ? '2px solid rgba(107, 207, 127, 0.4)' : '2px solid rgba(138, 149, 170, 0.4)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: category.visible ? '#6BCF7F' : '#8A95AA',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                {category.visible ? <Eye style={{ width: 14, height: 14 }} /> : <EyeOff style={{ width: 14, height: 14 }} />}
                {category.visible ? 'VISIBLE' : 'HIDDEN'}
              </button>
              <button
                onClick={() => handleEdit(category)}
                style={{
                  flex: 1,
                  background: 'rgba(255, 217, 61, 0.1)',
                  border: '2px solid rgba(255, 217, 61, 0.4)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#FFD93D',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Edit style={{ width: 14, height: 14 }} />
                EDIT
              </button>
              <button
                onClick={() => handleDelete(category)}
                style={{
                  flex: 1,
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '2px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#EF4444',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Trash2 style={{ width: 14, height: 14 }} />
                DELETE
              </button>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          background: 'rgba(0, 0, 0, 0.4)',
          borderRadius: '12px',
          border: '2px solid rgba(255, 20, 147, 0.3)'
        }}>
          <Package style={{ width: 48, height: 48, color: '#FF1493', opacity: 0.5, marginBottom: '16px' }} />
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '16px',
            fontWeight: 700,
            color: '#E0E6F0',
            margin: '0 0 8px 0',
            letterSpacing: '1px'
          }}>
            NO CATEGORIES FOUND
          </p>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '14px',
            color: '#8A95AA',
            margin: 0
          }}>
            Click "Add Category" to create your first category
          </p>
        </div>
      )}

      {/* Create/Edit Dialog */}
      {(showCreateDialog || editingCategory) && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95), rgba(36, 41, 56, 0.95))',
            backdropFilter: 'blur(12px)',
            border: '2px solid rgba(255, 20, 147, 0.4)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '20px',
                fontWeight: 700,
                color: '#FF1493',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0
              }}>
                {editingCategory ? 'EDIT CATEGORY' : 'CREATE CATEGORY'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateDialog(false)
                  setEditingCategory(null)
                  setFormData({ name: '', description: '', parent_id: '', copyParentFields: false })
                }}
                style={{
                  background: 'rgba(138, 149, 170, 0.1)',
                  border: '2px solid rgba(138, 149, 170, 0.4)',
                  borderRadius: '6px',
                  padding: '8px',
                  color: '#8A95AA',
                  cursor: 'pointer'
                }}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <form onSubmit={editingCategory ? handleUpdate : handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>CATEGORY NAME *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter category name"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>DESCRIPTION</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter category description"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>PARENT CATEGORY (OPTIONAL)</label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, parent_id: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">None (Top Level)</option>
                  {parentCategories
                    .filter(c => c.id !== editingCategory?.id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
              </div>

              {/* Copy Parent Fields Checkbox */}
              {formData.parent_id && !editingCategory && parentFieldsCount > 0 && (
                <div style={{
                  background: 'rgba(0, 240, 255, 0.1)',
                  border: '2px solid rgba(0, 240, 255, 0.3)',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.copyParentFields}
                      onChange={(e) => setFormData(prev => ({ ...prev, copyParentFields: e.target.checked }))}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: 700, color: '#00F0FF' }}>
                        Copy {parentFieldsCount} custom field{parentFieldsCount !== 1 ? 's' : ''} from parent
                      </div>
                      <div style={{ fontSize: '12px', color: '#8A95AA', marginTop: '4px' }}>
                        This will copy all custom field definitions to the new category
                      </div>
                    </div>
                  </label>
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255, 20, 147, 0.2)'
              }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    background: 'rgba(107, 207, 127, 0.2)',
                    border: '2px solid #6BCF7F',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#6BCF7F',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  {loading ? 'SAVING...' : (editingCategory ? 'UPDATE CATEGORY' : 'CREATE CATEGORY')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateDialog(false)
                    setEditingCategory(null)
                    setFormData({ name: '', description: '', parent_id: '', copyParentFields: false })
                  }}
                  style={{
                    background: 'rgba(138, 149, 170, 0.1)',
                    border: '2px solid rgba(138, 149, 170, 0.4)',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#8A95AA',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer'
                  }}
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Fields Slideout */}
      {showFieldsSlideout && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <div style={{
            width: '500px',
            background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '2px solid #00F0FF',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '32px',
                  fontWeight: 700,
                  color: '#00F0FF',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  margin: 0
                }}>
                  CUSTOM FIELDS
                </h2>
                <p style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '14px',
                  color: '#8A95AA',
                  margin: '4px 0 0 0'
                }}>
                  {selectedCategoryForFields?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowFieldsSlideout(false)
                  setSelectedCategoryForFields(null)
                  setCustomFields([])
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#8A95AA',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X style={{ width: 24, height: 24 }} />
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px'
            }}>
              {loadingFields ? (
                <div style={{ textAlign: 'center', padding: '48px' }}>
                  <Loader2 style={{ width: 48, height: 48, color: '#00F0FF', animation: 'spin 1s linear infinite' }} />
                </div>
              ) : (
                <>
                  {customFields.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '48px 24px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      borderRadius: '12px',
                      border: '2px solid rgba(0, 240, 255, 0.3)'
                    }}>
                      <Settings style={{ width: 48, height: 48, color: '#00F0FF', opacity: 0.5, marginBottom: '16px' }} />
                      <p style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '16px',
                        color: '#8A95AA',
                        margin: 0
                      }}>
                        No custom fields yet
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {customFields.map((field) => (
                        <div
                          key={field.id}
                          style={{
                            background: 'rgba(0, 0, 0, 0.4)',
                            border: '2px solid rgba(0, 240, 255, 0.3)',
                            borderRadius: '8px',
                            padding: '16px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <h3 style={{
                                fontFamily: 'Rajdhani, sans-serif',
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#00F0FF',
                                margin: '0 0 8px 0'
                              }}>
                                {field.name}
                              </h3>
                              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <span style={{
                                  fontFamily: 'Rajdhani, sans-serif',
                                  fontSize: '12px',
                                  color: '#8A95AA',
                                  textTransform: 'uppercase',
                                  background: 'rgba(138, 149, 170, 0.2)',
                                  padding: '4px 8px',
                                  borderRadius: '4px'
                                }}>
                                  {field.field_type}
                                </span>
                                {field.required && (
                                  <span style={{
                                    fontFamily: 'Rajdhani, sans-serif',
                                    fontSize: '12px',
                                    color: '#EF4444',
                                    textTransform: 'uppercase',
                                    background: 'rgba(239, 68, 68, 0.2)',
                                    padding: '4px 8px',
                                    borderRadius: '4px'
                                  }}>
                                    Required
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteField(field.id)}
                              disabled={loading}
                              style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '2px solid rgba(239, 68, 68, 0.4)',
                                borderRadius: '6px',
                                padding: '8px',
                                color: '#EF4444',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                opacity: loading ? 0.5 : 1
                              }}
                            >
                              <Trash2 style={{ width: 16, height: 16 }} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '24px',
              borderTop: '2px solid rgba(0, 240, 255, 0.3)',
              background: 'rgba(0, 0, 0, 0.4)'
            }}>
              {!showAddFieldDialog ? (
                <button
                  onClick={() => setShowAddFieldDialog(true)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    background: 'rgba(0, 240, 255, 0.2)',
                    border: '2px solid #00F0FF',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#00F0FF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Plus style={{ width: 16, height: 16 }} />
                  ADD FIELD
                </button>
              ) : (
                <div style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '12px',
                      color: '#8A95AA',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      Field Name *
                    </label>
                    <input
                      type="text"
                      value={fieldFormData.field_name}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, field_name: e.target.value })}
                      style={{
                        width: '100%',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '2px solid rgba(0, 240, 255, 0.3)',
                        borderRadius: '6px',
                        padding: '12px',
                        color: '#FFFFFF',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '14px'
                      }}
                      placeholder="Enter field name"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '12px',
                      color: '#8A95AA',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      Field Type
                    </label>
                    <select
                      value={fieldFormData.field_type}
                      onChange={(e) => setFieldFormData({ ...fieldFormData, field_type: e.target.value as CustomField['field_type'] })}
                      style={{
                        width: '100%',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '2px solid rgba(0, 240, 255, 0.3)',
                        borderRadius: '6px',
                        padding: '12px',
                        color: '#FFFFFF',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '14px'
                      }}
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                      <option value="boolean">Boolean</option>
                      <option value="select">Select</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '12px',
                      color: '#8A95AA',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={fieldFormData.required}
                        onChange={(e) => setFieldFormData({ ...fieldFormData, required: e.target.checked })}
                        style={{ width: '16px', height: '16px' }}
                      />
                      Required Field
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleAddField}
                      disabled={loading}
                      style={{
                        flex: 1,
                        background: 'rgba(0, 240, 255, 0.2)',
                        border: '2px solid #00F0FF',
                        borderRadius: '6px',
                        padding: '12px 20px',
                        color: '#00F0FF',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.5 : 1
                      }}
                    >
                      {loading ? 'SAVING...' : 'SAVE'}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddFieldDialog(false)
                        setFieldFormData({ field_name: '', field_type: 'text', required: false, sort_order: 0 })
                      }}
                      disabled={loading}
                      style={{
                        flex: 1,
                        background: 'rgba(138, 149, 170, 0.1)',
                        border: '2px solid rgba(138, 149, 170, 0.4)',
                        borderRadius: '6px',
                        padding: '12px 20px',
                        color: '#8A95AA',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.5 : 1
                      }}
                    >
                      CANCEL
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
